import {
  ALT_TEXT_KEY_SUFFIX,
  DEFAULT_LAYOUT_CONTEXT,
  DEFAULT_RENDER_MODE,
  FIELD_TYPE,
  GRID_FIELD,
  GRID_FIELD_BASES,
  gridFieldKey,
  LAYOUT_PRESENTATION,
  POSITION_KEY_SUFFIX,
  RESERVED_AUTO_INJECTED_TEMPLATE_FIELD_KEYS,
  TABLE_COLUMN_TYPE,
  TABLE_COLUMN_TYPES,
  TEXT_FIELD_TYPES,
} from '../vocabulary.js';

/** @typedef {import('../../shell/types/client.types.js').ClientApiResponse} ClientApiResponse */
/** @typedef {import('../../shell/types/client.types.js').ClientViewModel} ClientViewModel */

export function isReservedAutoInjectedTemplateFieldKey(key) {
  return RESERVED_AUTO_INJECTED_TEMPLATE_FIELD_KEYS.includes(key);
}

export function isAutoGeneratedTemplateFieldKey(key) {
  return isReservedAutoInjectedTemplateFieldKey(key) || key.endsWith(ALT_TEXT_KEY_SUFFIX) || key.endsWith(POSITION_KEY_SUFFIX);
}

export function sanitizeTemplateFields(fields = []) {
  return fields.filter((field) => !isReservedAutoInjectedTemplateFieldKey(field.key));
}

/**
 * Splits an options textarea (one option per line) into a list of trimmed, non-blank options.
 * Commas are kept, so an option such as "Te koop, met optie" survives a round trip.
 *
 * @param {string | null | undefined} text
 * @returns {string[]}
 */
export function parseColumnOptions(text) {
  return String(text ?? '')
    .split(/\r?\n/)
    .map((option) => option.trim())
    .filter((option) => option.length > 0);
}

/**
 * @param {string[] | null | undefined} options
 * @returns {string} one option per line
 */
export function formatColumnOptions(options) {
  return Array.isArray(options) ? options.join('\n') : '';
}

/**
 * Converts a stored table column into the editable column state used by the column editor.
 * Options are kept as the raw textarea text (one option per line) so typing is not disrupted.
 *
 * @param {import('../../shell/types/client.types.js').TableColumnType} column
 * @returns {import('../../shell/types/client.types.js').TableColumnState}
 */
export function toTableColumnState(column) {
  return {
    key: column.key || '',
    label: column.label || '',
    type: TABLE_COLUMN_TYPES.includes(column.type) ? column.type : TABLE_COLUMN_TYPE.TEXT,
    required: Boolean(column.required),
    options: formatColumnOptions(column.options),
  };
}

const TABLE_COLUMN_KEY_PATTERN = /^[a-z][a-z0-9_]*$/;

/**
 * Validates the column editor state of one table field with the same rules the backend applies
 * when saving a template, so a bad column is flagged before the request is sent. Values are
 * checked after the trimming that {@link toTableColumnPayload} applies. The reserved row key
 * `_id` is ruled out by the key pattern, which requires a leading lowercase letter.
 *
 * @param {string} fieldKey
 * @param {{columns?: import('../../shell/types/client.types.js').TableColumnState[], maxRows?: string | number | null}} config
 * @returns {import('../../shell/types/client.types.js').TableFieldValidationError | null}
 */
export function validateTableFieldConfig(fieldKey, config) {
  const columns = config.columns || [];
  if (columns.length === 0) {
    return {message: `Table field '${fieldKey}' must declare at least one column.`, columnIndex: null, prop: 'type'};
  }
  const maxRows = String(config.maxRows ?? '').trim();
  if (maxRows && !(Number.isInteger(Number(maxRows)) && Number(maxRows) >= 1)) {
    return {message: `Table field '${fieldKey}' must have 'maxRows' of at least 1.`, columnIndex: null, prop: 'maxRows'};
  }
  const keys = new Set();
  for (const [columnIndex, column] of columns.entries()) {
    const error = validateTableColumn(fieldKey, column);
    if (error) {
      return {...error, columnIndex};
    }
    const key = String(column.key ?? '').trim();
    if (keys.has(key)) {
      return {message: `Table field '${fieldKey}' has duplicate column key '${key}'.`, columnIndex, prop: 'key'};
    }
    keys.add(key);
  }
  return null;
}

/**
 * @param {string} fieldKey
 * @param {import('../../shell/types/client.types.js').TableColumnState} column
 * @returns {{message: string, prop: 'key' | 'label' | 'options'} | null}
 */
function validateTableColumn(fieldKey, column) {
  const key = String(column.key ?? '').trim();
  if (!TABLE_COLUMN_KEY_PATTERN.test(key)) {
    return {
      message: `Table field '${fieldKey}' has invalid column key '${key}': it must start with a lowercase letter `
        + 'and contain only lowercase letters, digits, and underscores.',
      prop: 'key',
    };
  }
  const prefix = `Column '${key}' of table field '${fieldKey}'`;
  if (!String(column.label ?? '').trim()) {
    return {message: `${prefix} must have a label.`, prop: 'label'};
  }
  if (column.type !== TABLE_COLUMN_TYPE.SELECT) {
    return null;
  }
  const options = parseColumnOptions(column.options);
  if (options.length === 0) {
    return {message: `${prefix} must declare at least one option.`, prop: 'options'};
  }
  const duplicate = options.find((option, index) => options.indexOf(option) !== index);
  if (duplicate !== undefined) {
    return {message: `${prefix} has duplicate option '${duplicate}'.`, prop: 'options'};
  }
  return null;
}

/**
 * Converts editable column state into the API shape. `options` is only present on select columns.
 *
 * @param {import('../../shell/types/client.types.js').TableColumnState} column
 * @returns {import('../../shell/types/client.types.js').TableColumnType}
 */
export function toTableColumnPayload(column) {
  const payload = {
    key: String(column.key ?? '').trim(),
    label: String(column.label ?? '').trim(),
    type: column.type,
    required: Boolean(column.required),
  };
  if (column.type === TABLE_COLUMN_TYPE.SELECT) {
    payload.options = parseColumnOptions(column.options);
  }
  return payload;
}

/**
 * Builds the API shape of one template field from the editor state. `columns` and `maxRows` are
 * only emitted for table fields, since the backend rejects them on any other type. An empty
 * `maxRows` input means "no limit" and is omitted. `recommendedLength` is only emitted for text
 * and textarea fields, as a number, and omitted when empty.
 *
 * @param {string} key
 * @param {{label: string, type: string, required: boolean, editorGroup?: string | null, help?: string | null, recommendedLength?: string | number | null, dependsOn?: string | null, options?: string, columns?: import('../../shell/types/client.types.js').TableColumnState[], maxRows?: string | number | null}} config
 * @returns {import('../../shell/types/client.types.js').TemplateFieldType}
 */
export function toTemplateFieldPayload(key, config) {
  const field = {
    key,
    label: config.label,
    type: config.type,
    required: config.required,
  };
  const editorGroup = String(config.editorGroup ?? '').trim();
  if (editorGroup) field.editorGroup = editorGroup;
  const help = String(config.help ?? '').trim();
  if (help) field.help = help;
  const recommendedLength = String(config.recommendedLength ?? '').trim();
  if (recommendedLength && TEXT_FIELD_TYPES.includes(config.type)) field.recommendedLength = Number(recommendedLength);
  const dependsOn = String(config.dependsOn ?? '').trim();
  if (dependsOn) field.dependsOn = dependsOn;
  if (config.type === FIELD_TYPE.SELECT) {
    field.options = parseColumnOptions(config.options);
  }
  if (config.type !== FIELD_TYPE.TABLE) {
    return field;
  }
  field.columns = (config.columns || []).map(toTableColumnPayload);
  const maxRows = String(config.maxRows ?? '').trim();
  if (maxRows) {
    field.maxRows = Number(maxRows);
  }
  return field;
}

/**
 * @param {ClientApiResponse} apiResponse
 * @returns {ClientViewModel}
 */
export function toClientViewModel(apiResponse) {
  return {
    ...apiResponse,
    templateCount: apiResponse.templates.length,
  };
}

/**
 * @param {ClientApiResponse[]} apiResponses
 * @returns {ClientViewModel[]}
 */
export function toClientViewModelList(apiResponses) {
  return apiResponses.map(toClientViewModel);
}

/**
 * @param {{ slug: string, name: string, repoName: string, basePath: string, domain: string, userCreationGuid?: string }} formData
 * @returns {{ slug: string, name: string, repoName: string, basePath: string, domain: string, userCreationGuid: string }}
 */
export function toCreateClientRequest(formData) {
  return {
    slug: formData.slug.trim().toLowerCase(),
    name: formData.name.trim(),
    repoName: formData.repoName.trim(),
    domain: (formData.domain || '').trim(),
    basePath: (formData.basePath || '').trim(),
    userCreationGuid: (formData.userCreationGuid || '').trim(),
  };
}

/**
 * @param {{ name: string, content: string, description: string, mode?: string, singleInstance?: boolean, homepage?: boolean, indexedByField?: string | null, fields: Array<any> }} formData
 * @returns {{ name: string, content: string, description: string, mode: string, singleInstance: boolean, homepage: boolean, indexedByField: string | null, fields: Array<any> }}
 */
export function toAddTemplateRequest(formData) {
    return {
        name: formData.name.trim().toLowerCase(),
        content: formData.content.trim(),
        description: formData.description.trim(),
        mode: formData.mode || DEFAULT_RENDER_MODE,
        singleInstance: Boolean(formData.singleInstance),
        homepage: Boolean(formData.homepage),
        slugPrefix: (formData.slugPrefix || '').trim() || null,
        layout: formData.layout || null,
        breadcrumbs: formData.breadcrumbs || null,
        indexedByField: (formData.indexedByField || '').trim() || null,
        composition: formData.composition || null,
        fields: sanitizeTemplateFields(formData.fields),
    };
}

/**
 * @param {{ content: string, description: string, mode?: string, singleInstance?: boolean, homepage?: boolean, indexedByField?: string | null, fields: Array<any> }} formData
 * @returns {{ content: string, description: string, mode: string, singleInstance: boolean, homepage: boolean, indexedByField: string | null, fields: Array<any> }}
 */
export function toUpdateTemplateRequest(formData) {
    return {
        content: formData.content.trim(),
        description: (formData.description || '').trim(),
        mode: formData.mode || DEFAULT_RENDER_MODE,
        singleInstance: Boolean(formData.singleInstance),
        homepage: Boolean(formData.homepage),
        slugPrefix: (formData.slugPrefix || '').trim() || null,
        layout: formData.layout || null,
        breadcrumbs: formData.breadcrumbs || null,
        indexedByField: (formData.indexedByField || '').trim() || null,
        composition: formData.composition || null,
        fields: sanitizeTemplateFields(formData.fields),
    };
}

/**
 * Parses the advanced section-composition JSON editor. An empty editor means "no composition".
 * Only the outer shape is checked here; the backend validates the full contract and returns
 * a descriptive error.
 *
 * @param {string} text
 * @returns {{composition: import('../../shell/types/client.types.js').TemplateComposition | null, error: string | null}}
 */
export function parseCompositionJson(text) {
  const trimmed = (text || '').trim();
  if (!trimmed) {
    return {composition: null, error: null};
  }
  let parsed;
  try {
    parsed = JSON.parse(trimmed);
  } catch (e) {
    return {composition: null, error: `Composition is not valid JSON: ${e.message}`};
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {composition: null, error: 'Composition must be a JSON object.'};
  }
  if (parsed.version !== 1) {
    return {composition: null, error: 'Composition "version" must be 1.'};
  }
  if (!Array.isArray(parsed.sections) || parsed.sections.length === 0) {
    return {composition: null, error: 'Composition "sections" must be a non-empty array.'};
  }
  return {composition: parsed, error: null};
}

/**
 * Re-indents the text of the section-composition JSON editor. An empty editor stays empty.
 *
 * @param {string} text
 * @returns {{text: string, error: string | null}} the formatted text, or the unchanged text with
 *   the parse error when it is not valid JSON
 */
export function formatCompositionText(text) {
  const trimmed = (text || '').trim();
  if (!trimmed) {
    return {text: '', error: null};
  }
  try {
    return {text: JSON.stringify(JSON.parse(trimmed), null, 2), error: null};
  } catch (e) {
    return {text, error: `Composition is not valid JSON: ${e.message}`};
  }
}

/**
 * @param {import('../../shell/types/client.types.js').TemplateComposition | null | undefined} composition
 * @returns {string}
 */
export function formatCompositionJson(composition) {
  return composition ? JSON.stringify(composition, null, 2) : '';
}

const PREFIXED_GRID_FIELD_KEY_PATTERN = new RegExp(`^([a-zA-Z0-9_]+)_(${GRID_FIELD_BASES.join('|')})$`);

export function inferTemplateLayoutContexts(fields = [], existingContexts = []) {
  const fieldKeys = new Set(fields.map((field) => field.key));
  const contexts = new Set(existingContexts);

  if (fieldKeys.has(gridFieldKey(DEFAULT_LAYOUT_CONTEXT, GRID_FIELD.ROW))
    && fieldKeys.has(gridFieldKey(DEFAULT_LAYOUT_CONTEXT, GRID_FIELD.COLUMN))) {
    contexts.add(DEFAULT_LAYOUT_CONTEXT);
  }
  for (const key of fieldKeys) {
    const match = key.match(PREFIXED_GRID_FIELD_KEY_PATTERN);
    if (!match) {
      continue;
    }
    // The prefix is matched literally, so `homepage_row` counts for the default context too.
    const [, context] = match;
    if (fieldKeys.has(`${context}_${GRID_FIELD.ROW}`) && fieldKeys.has(`${context}_${GRID_FIELD.COLUMN}`)) {
      contexts.add(context);
    }
  }

  if (contexts.has(DEFAULT_LAYOUT_CONTEXT)) {
    return [DEFAULT_LAYOUT_CONTEXT, ...[...contexts].filter((context) => context !== DEFAULT_LAYOUT_CONTEXT).sort()];
  }
  return [...contexts].sort();
}

export function toTemplateLayoutConfig(fields = [], enabledByFieldByContext = new Map(), presentationByContext = new Map(), columnsByContext = new Map()) {
  const contexts = inferTemplateLayoutContexts(
    fields,
    enabledByFieldByContext instanceof Map ? [...enabledByFieldByContext.keys()] : Object.keys(enabledByFieldByContext || {})
  );
  if (contexts.length === 0) {
    return null;
  }

  const resolvedContexts = {};
  for (const context of contexts) {
    const enabledByField = enabledByFieldByContext instanceof Map
      ? (enabledByFieldByContext.get(context) || '').trim()
      : String(enabledByFieldByContext?.[context] || '').trim();
    const presentation = presentationByContext instanceof Map
      ? presentationByContext.get(context)
      : presentationByContext?.[context];
    const columns = columnsByContext instanceof Map
      ? columnsByContext.get(context)
      : columnsByContext?.[context];
    resolvedContexts[context] = {
      ...(enabledByField ? {enabledByField} : {}),
      // Grid is the backend default, so only a carousel presentation is sent.
      ...(presentation === LAYOUT_PRESENTATION.CAROUSEL ? {presentation} : {}),
      ...(presentation !== LAYOUT_PRESENTATION.CAROUSEL && columns !== '' && columns != null && Number.isInteger(Number(columns))
        && Number(columns) >= 1 && Number(columns) <= 10 ? {columns: Number(columns)} : {}),
    };
  }
  return {contexts: resolvedContexts};
}

/**
 * Converts a stored layout config into the per-context editor state that
 * {@link toTemplateLayoutConfig} turns back into the API shape. A context without a presentation
 * edits as a grid.
 *
 * @param {import('../../shell/types/client.types.js').TemplateLayoutConfig | null | undefined} layout
 * @returns {{enabledByField: Map<string, string>, presentationByContext: Map<string, string>, columnsByContext: Map<string, number | string>}}
 */
export function toLayoutEditorState(layout) {
  const entries = Object.entries(layout?.contexts || {});
  return {
    enabledByField: new Map(entries.map(([context, config]) => [context, config?.enabledByField || ''])),
    presentationByContext: new Map(entries.map(([context, config]) => [context, config?.presentation || LAYOUT_PRESENTATION.GRID])),
    columnsByContext: new Map(entries.map(([context, config]) => [context, config?.columns || ''])),
  };
}

/**
 * @param {import('../../shell/types/client.types.js').UserApiResponse[]} apiResponses
 * @returns {import('../../shell/types/client.types.js').UserViewModel[]}
 */
export function toUserViewModelList(apiResponses) {
  return apiResponses.map((u) => ({ ...u }));
}
